import { AnyObject } from '@store/types';
import { observable, action, computed, makeObservable } from 'mobx';
import isNil from 'lodash/isNil';

import type { Optional, PriceData, PriceQuery } from 'chaika-frontend-types/frontend-cms';
import { PriceStatus } from 'chaika-enums';

import { SearchList } from '@store/abstract/SearchList';
import { DateHelper } from '@utils/DateHelper';
import { assertIsDefined } from '@utils/assertIsDefined';

import { PriceList } from './PriceList';
import { PurchasePriceList } from './PurchasePriceList';
import { Price, PriceModelData } from './Price';
import { Money } from '@store/models/Money';

export class AbstractPriceStore {
    @observable public legalEntityPriceListId?: string;
    @observable public priceList = new PriceList();
    @observable public purchasePriceList = new PurchasePriceList();
    @observable public searchList = new SearchList<Price, PriceData, PriceQuery>(PriceList);
    @observable public compositeServiceList = new PriceList();

    constructor() {
        makeObservable(this);
    }

    @computed
    public get searchParams() {
        return {
            ...this.searchList.searchParams,
        };
    }

    public getEditTableListDataWithCount(): { prices: PriceModelData[]; count: number } {
        const list = this.searchList.searchParamsSet ? this.searchList : this.priceList;

        return {
            prices: list.values.map(price => price.makeEditTableData()),
            count: list.count,
        };
    }

    public getFilteredEditTableListDataWithCount(): { prices: PriceModelData[]; count: number } {
        const list = this.searchList.searchParamsSet ? this.searchList : this.priceList;
        const rawFilterParams: AnyObject = this.searchList.searchParamsSet
            ? this.searchList.queryParams
            : this.priceList.getFilterParams();

        // Make a copy to avoid modifying the object in the MobX store
        const filterParams = { ...rawFilterParams };

        for (const key in filterParams) {
            if (filterParams[key] === 'null') {
                filterParams[key] = undefined;
            }
        }

        // Проблема из за типа serviceId
        // Массив в типе PriceQuery трансформируется в строку и типизация идет по строке.
        // @ts-ignore
        const filteredValues = list.getFilteredValues(filterParams);

        return {
            prices: filteredValues.map(price => price.makeEditTableData()),
            count: list.count,
        };
    }

    public getFilterParams(): PriceQuery {
        return this.searchList.searchParams;
    }

    @action
    public resetQuery(): void {
        this.searchList.reset();
    }

    public getPrice(id: string): Optional<Price> {
        return this.priceList.get(id) || this.searchList.get(id);
    }

    public getPriceFromPriceList(id: string): Price {
        return this.priceList.getExisting(id);
    }

    public getPriceFromSearchList(id: string): Optional<Price> {
        return this.searchList.get(id);
    }

    public getBasePrice(serviceId: string, legalEntityIds: string[] = []): Optional<Price> {
        return this.priceList.values.find(
            price =>
                price.serviceId === serviceId &&
                price.legalEntityIds.length === legalEntityIds.length &&
                price.legalEntityIds.every(id => legalEntityIds.includes(id)) &&
                isNil(price.userId) &&
                price.status === PriceStatus.Active,
        );
    }

    public getCompositeServiceTotalPrice(includedServices: string[], legalEntityIds?: string[]): Money {
        const prices = includedServices.map(id => this.getBasePrice(id, legalEntityIds)?.value);

        return new Money(prices.reduce((acc, price) => acc + (price?.kopecks ?? 0), 0));
    }

    public findBasePricesByLegalEntities(serviceId: string, legalEntityIds: string[]): Price[] {
        return this.priceList.values.filter(
            price =>
                price.serviceId === serviceId &&
                price.legalEntityIds.some(id => legalEntityIds.includes(id)) &&
                isNil(price.userId) &&
                price.status === PriceStatus.Active,
        );
    }

    public findBasePrices(serviceId: string): Price[] {
        return this.priceList.values.filter(
            price => price.serviceId === serviceId && isNil(price.userId) && price.status === PriceStatus.Active,
        );
    }

    public delete(id: string): void {
        this.priceList.remove(id);
        this.searchList.remove(id);
    }

    public getAvailableServiceForCompositePrice(compositeServiceId: string): Price[] {
        const compositeService = this.compositeServiceList.getPriceByServiceId(compositeServiceId);

        return compositeService ? this.getAvailableServiceOnDate(compositeService.scheduledOn) : [];
    }

    public getAvailableServiceOnDate(scheduledOn: string | Date): Price[] {
        return this.priceList.values.filter(
            price =>
                !price.isComposite && price.isActive() && DateHelper.isSameOrBefore(price.scheduledOn, scheduledOn),
        );
    }

    public getAvailableServiceForCompositePriceFromSearchList(compositeServiceId: string): Price[] {
        const compositeService = this.compositeServiceList.getPriceByServiceId(compositeServiceId);
        assertIsDefined(compositeService, `Composite service ${compositeServiceId} price is loaded`);

        return this.getAvailableServiceOnDateFromSearchList(compositeService.scheduledOn);
    }

    public getAvailableServiceOnDateFromSearchList(scheduledOn: string | Date): Price[] {
        return this.searchList.values.filter(
            price =>
                !price.isComposite && price.isActive() && DateHelper.isSameOrBefore(price.scheduledOn, scheduledOn),
        );
    }
}
